/**
 * Client-side pitch + key helpers used by the Loop Station.
 * All numpy-free — runs entirely in the browser.
 */

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/**
 * Detect the dominant note of an audio blob using autocorrelation over the
 * first ~4 seconds. Returns { note, midi, freq } or null on silence / noise.
 */
export async function detectDominantNote(blob) {
  try {
    const ArrayBufferView = await blob.arrayBuffer();
    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    const buf = await ctx.decodeAudioData(ArrayBufferView);
    ctx.close?.();
    const sr = buf.sampleRate;
    const chunk = Math.min(buf.length, sr * 4);
    const data = buf.getChannelData(0).slice(0, chunk);
    const freq = autoCorrelateFundamental(data, sr);
    if (!freq) return null;
    const midi = 69 + 12 * Math.log2(freq / 440);
    const midiRound = Math.round(midi);
    if (midiRound < 24 || midiRound > 96) return null; // 24=C1, 96=C7 — outside is likely noise
    return {
      note: NOTE_NAMES[((midiRound % 12) + 12) % 12],
      midi: midiRound,
      freq: Math.round(freq * 10) / 10,
    };
  } catch {
    return null;
  }
}

/**
 * Basic time-domain autocorrelation. Good enough for single-instrument or
 * vocal loops; struggles with dense harmony (that's OK — the DJ can override).
 */
function autoCorrelateFundamental(buf, sr) {
  const SIZE = buf.length;
  // RMS gate — bail out on silence
  let rms = 0;
  for (let i = 0; i < SIZE; i++) rms += buf[i] * buf[i];
  rms = Math.sqrt(rms / SIZE);
  if (rms < 0.01) return null;
  const MIN_F = 60, MAX_F = 1200;
  const minLag = Math.floor(sr / MAX_F);
  const maxLag = Math.floor(sr / MIN_F);
  let bestLag = -1, bestCorr = 0;
  for (let lag = minLag; lag <= maxLag; lag++) {
    let corr = 0;
    for (let i = 0; i < SIZE - lag; i++) corr += buf[i] * buf[i + lag];
    if (corr > bestCorr) { bestCorr = corr; bestLag = lag; }
  }
  if (bestLag <= 0) return null;
  return sr / bestLag;
}

/**
 * Semitones between two note names (returns a signed number ≥-6, ≤6 — we
 * pick the shorter direction so a small key match doesn't slow the loop to
 * half-speed / double-speed).
 */
export function semitoneOffset(from, to) {
  if (!from || !to) return 0;
  const a = NOTE_NAMES.indexOf(from);
  const b = NOTE_NAMES.indexOf(to);
  if (a < 0 || b < 0) return 0;
  let diff = b - a;
  while (diff > 6) diff -= 12;
  while (diff < -6) diff += 12;
  return diff;
}

export function playbackRateForSemitones(n) {
  return Math.pow(2, n / 12);
}

export const NOTE_LIST = NOTE_NAMES.slice();
